import React, { useState, useEffect, useRef } from 'react'
import { Toast } from 'primereact/toast'
import { Button } from 'primereact/button'
import performanceService from '../../services/data/performance.service'

const STEP_LABELS = {
    AA: 'Auto Avaliação',
    AG: 'Avaliação do Gestor',
    AP: 'Avaliação de Pares',
    AS: 'Avaliar Superior',
    RN: 'Reunião 1:1',
}

const STEPS_BY_TYPE = {
    PD: ['AA'],
    RD: ['RN'],
    BD: ['AG', 'RN'],
    AD: ['AA', 'AG', 'RN'],
    SD: ['AA', 'AG', 'RN'],
    TD: ['AA', 'AG', 'AP', 'RN'],
}

const ALL_STEPS = ['AA', 'AS', 'AG', 'AP', 'RN']

export default function StepWeights({ templateId, reviewId, assessmentType, readOnly = false }) {
    const toast = useRef(null)

    const activeSteps = STEPS_BY_TYPE[assessmentType] ?? ALL_STEPS
    const editableSteps = activeSteps.slice(0, -1)
    const lastStep = activeSteps[activeSteps.length - 1]

    const [weights, setWeights] = useState(
        activeSteps.reduce((acc, code) => ({ ...acc, [code]: code === lastStep ? 100 : 0 }), {})
    )
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    const othersSum = editableSteps.reduce((sum, code) => sum + (parseInt(weights[code]) || 0), 0)
    const lastWeight = Math.max(0, 100 - othersSum)

    useEffect(() => {
        if (!templateId && !reviewId) return
        setLoading(true)
        performanceService.listStepWeights(templateId, reviewId).then((data) => {
            if (data && data.length > 0) {
                const map = {}
                data.forEach((w) => { map[w.step_type] = w.weight })
                setWeights((prev) => {
                    const next = { ...prev }
                    activeSteps.forEach((code) => {
                        if (map[code] !== undefined) next[code] = map[code]
                    })
                    return next
                })
            }
        }).finally(() => setLoading(false))
    }, [templateId, reviewId, assessmentType])

    function handleChange(code, value) {
        const parsed = Math.min(100, Math.max(0, parseInt(value) || 0))
        setWeights((prev) => ({ ...prev, [code]: parsed }))
    }

    function handleSave() {
        setSaving(true)
        const payload = [
            ...editableSteps.map((code) => ({
                step_type: code,
                weight: parseInt(weights[code]) || 0,
            })),
            { step_type: lastStep, weight: lastWeight },
        ]
        performanceService.saveStepWeights(templateId, payload, reviewId).then(() => {
            toast.current.show({ severity: 'success', summary: 'Salvo', detail: 'Pesos salvos com sucesso.', life: 3000 })
            setWeights((prev) => ({ ...prev, [lastStep]: lastWeight }))
        }).catch((err) => {
            const msg = err?.response?.data ? JSON.stringify(err.response.data) : 'Erro ao salvar pesos.'
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: msg, life: 5000 })
        }).finally(() => setSaving(false))
    }

    if (loading) return <p className="text-muted small">Carregando pesos...</p>

    if (activeSteps.length === 1) return null

    return (
        <div>
            <Toast ref={toast} position="top-right" />

            <div className="border rounded overflow-hidden mb-2">
                <table className="table mb-0">
                    <thead className="border-bottom">
                        <tr>
                            <th style={{ width: '60%' }}>Etapa</th>
                            <th style={{ width: '40%' }}>Peso (%)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {editableSteps.map((code) => (
                            <tr key={code}>
                                <td className="align-middle">{STEP_LABELS[code]}</td>
                                <td>
                                    {readOnly ? (
                                        <span>{weights[code]}%</span>
                                    ) : (
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            min={0}
                                            max={100}
                                            value={weights[code]}
                                            onChange={(e) => handleChange(code, e.target.value)}
                                        />
                                    )}
                                </td>
                            </tr>
                        ))}
                        <tr key={lastStep} className="table-light">
                            <td className="align-middle">
                                {STEP_LABELS[lastStep]}
                                {!readOnly && <small className="text-muted ms-1">(automático)</small>}
                            </td>
                            <td className="align-middle fw-semibold">
                                {readOnly ? weights[lastStep] : lastWeight}%
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr className="fw-bold table-success">
                            <td>Total</td>
                            <td>100%</td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {!readOnly && (
                <Button
                    label="Salvar"
                    size="small"
                    rounded
                    disabled={saving}
                    loading={saving}
                    onClick={handleSave}
                    className="px-4"
                />
            )}

            <p className="text-muted small mt-2">
                Etapas com peso 0 participam do ciclo mas não entram no cálculo da nota final.
            </p>
        </div>
    )
}
